import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './TableBulkActions.module.css';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';

import { tr } from '@/i18n';

/** Slot under the page's scroll area in the app shell (MainLayout). A bar rendered there takes its own
 *  strip at the bottom of the window: the scroll area ends above it, so it never covers table rows. */
export const PAGE_BOTTOM_BAR_ID = 'page-bottom-bar';

interface TableBulkActionsProps {
  selectedCount: number;
  onClear: () => void;
  isLoading?: boolean;
  children: React.ReactNode;
  /** Positioning for the floating fallback only (pages outside the app shell). */
  style?: React.CSSProperties;
}

export const TableBulkActions: React.FC<TableBulkActionsProps> = ({
  selectedCount,
  onClear,
  isLoading = false,
  children,
  style
}) => {
  const showBar = selectedCount > 0;
  // The shell mounts before the page, so the slot is normally there on the first render.
  const [slot, setSlot] = useState<HTMLElement | null>(() =>
    typeof document === 'undefined' ? null : document.getElementById(PAGE_BOTTOM_BAR_ID),
  );
  useEffect(() => {
    if (!slot) setSlot(document.getElementById(PAGE_BOTTOM_BAR_ID));
  }, [slot]);

  const bar = (
    <div className={styles.bar}>
      <div className={styles.selectionInfo}>

        <Checkbox
          className={styles.checkboxWrapper}
          checked={true}
          onChange={onClear}
          disabled={isLoading}
          variant="dark20x20"
        />

        <span className={`${styles.countText} h2`}>
          {selectedCount} {selectedCount === 1 ? tr("ui.item", "item") : tr("ui.items", "items")} {tr("ui.selected_ef7de3", "selected")}
        </span>
      </div>

      <div className={styles.actionsWrapper}>
        {children}
      </div>
    </div>
  );

  // Docked under the scroll area when the shell provides the slot (never over the rows).
  if (slot) {
    return showBar ? createPortal(<div className={styles.docked}>{bar}</div>, slot) : null;
  }

  return (
    <div
      className={`${styles.container} ${showBar ? styles.show : ''}`}
      style={style}
    >
      {bar}
    </div>
  );
};

export { styles as bulkActionStyles };
